import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  contentTranslations,
  type TranslationRefDoctype,
} from "@/lib/erp/contentTranslations";
import { itemQueryKeys } from "@/features/inventory/api/itemQueryKeys";
import { useAuthStore } from "@/lib/store/authStore";

export const translationQueryKey = (
  refDoctype: string,
  refName: string,
  tenant: string,
) => ["content-translations", refDoctype, refName, tenant || "__primary__"] as const;

export function useItemTranslations(
  refDoctype: TranslationRefDoctype,
  refName: string,
  enabled = true,
) {
  // Scope translations to the store the user is editing — same key the
  // Storefront Settings > Languages page uses (useStorefrontTenantSettings).
  // null → backend resolves the company's PRIMARY store deterministically.
  const activeStorefrontKey = useAuthStore((s) => s.activeStorefrontKey);
  const tenant = activeStorefrontKey ?? "";
  return useQuery({
    queryKey: translationQueryKey(refDoctype, refName, tenant),
    queryFn: () => contentTranslations.get(refDoctype, refName, tenant),
    enabled: enabled && !!refName,
  });
}

export function useSaveItemTranslations(
  refDoctype: TranslationRefDoctype,
  refName: string,
) {
  const queryClient = useQueryClient();
  const activeStorefrontKey = useAuthStore((s) => s.activeStorefrontKey);
  const tenant = activeStorefrontKey ?? "";
  return useMutation({
    mutationFn: (translations: Record<string, Record<string, string | null>>) =>
      contentTranslations.save(refDoctype, refName, translations, tenant),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: translationQueryKey(refDoctype, refName, tenant),
      });
      if (refDoctype === "Item") {
        queryClient.invalidateQueries({ queryKey: itemQueryKeys.detail(refName) });
      }
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Could not save translations");
    },
  });
}
